import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import RestartIcon from '@material-ui/icons/Replay';
import AppActions from '../../actions/app-actions';
import { useTimeout, useTableState } from '../../custom-hooks';
import Loader from '../common/loader';
import { showError, isViewerUser } from '../../helpers';
import CTable from '../common/ctable';
import { columns } from './vcams-table-def';
import VcamsExpanded from './vcams-expanded';
import VcamsDialog from './vcams-dialog';
import VcamsFilter from './vcams-filter';
import { AppContext } from '../../contexts/app-context';
import VcamsDuplicateDialog from './vcams-duplicate-dialog';
import VcamsRemoveDialog from './vcams-remove-dialog';
import AppStore from '../../stores/app-store';
import Dropzone from 'react-dropzone';
import WarningIcon from '@material-ui/icons/Warning';
import moment from 'moment';
import FileIcon from '@material-ui/icons/CloudUpload';
import {LinearProgress} from '@material-ui/core';

const timeoutInMilliSec = 10 * 1000;

const VcamsTab = (props) => {
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [list, setList] = useState([]);
  const [infraList, setInfraList] = useState([]);
  const [infraNameList, setInfraNameList] = useState([]);
  const [uuidList, setUuidList] = useState([]);
  const [filterSelectedType, setFilterSelectedType] = useState('All');
  const [filterselectedItem, setFilterSelectedItem] = useState('');
  const [dlg, setDlg] = useState({ open: false });
  const [removeDlg, setRemoveDlg] = useState({ open: false });
  const [duplicateDlg, setDuplicateDlg] = useState({ open: false });
  const [startTimer] = useTimeout(refreshData, timeoutInMilliSec);
  const [showDropzone, setShowDropzone] = useState(false);
  const [dbSptsStatus, setDbSptsStatus] = useState(false);
  const [isProgress, setIsProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const { colDef, selectedRows, onFilterChange, onColumnChange, onRowSelect, onAllRowSelect, onSelectedRowsReset } = useTableState(columns());

  function getVcByFilteredType () {
    if (filterSelectedType === 'All')
      return AppActions.getAllVC();
    else if (filterSelectedType === 'By EWC')
      return AppActions.getVcByInfraName(filterselectedItem);
    else
      return AppActions.getVcByUuid(filterselectedItem);
  }

  const onAllSelected = useCallback(() => {
    setPageLoading(true);
    setFilterSelectedType('All');
    setFilterSelectedItem('');
    AppActions.getAllVC()
      .then(res => {
        ReactDOM.unstable_batchedUpdates(() => {
          setList(res || []);
          setPageLoading(false);
        });
      })
      .catch(err => {
        setPageLoading(false);
        showError(err, `Cant' get the vcam`);
      });
  }, []);

  const onInfraNameSelected = useCallback(selected => {
    setPageLoading(true);
    setFilterSelectedType('By EWC');
    setFilterSelectedItem(selected);
    AppActions.getVcByInfraName(selected)
      .then(res => {
        ReactDOM.unstable_batchedUpdates(() => {
          setList(res || []);
          setPageLoading(false);
        });
      })
      .catch(err => {
        setPageLoading(false);
        showError(err, `Can't get the vcam`);
      });
  }, []);

  const onUuidSelected = useCallback(selected => {
    setPageLoading(true);
    setFilterSelectedType('By Device');
    setFilterSelectedItem(selected);
    AppActions.getVcByUuid(selected)
      .then(res => {
        ReactDOM.unstable_batchedUpdates(() => {
          setList(res.body || []);
          setPageLoading(false);
        });
      })
      .catch(err => {
        setPageLoading(false);
        showError(err, `Can't get the vcam`);
      })
  }, []);

  const onDeploy = useCallback(rowData => {
    const onCancel = () => setDlg(() => ({ open: false }));
    function onOk (ewcName, uuid) {
      const promises = uuid ? [AppActions.updateVcamEwc(uuid, ewcName)] : selectedRows.map(d => AppActions.updateVcamEwc(d.uuid, ewcName));
      Promise.all(promises)
        .then(() => getVcByFilteredType())
        .then(res => {
          ReactDOM.unstable_batchedUpdates(() => {
            setList(res || []);
            setDlg(() => ({ open: false }));
            const msg = uuid ? `Successfully VC(${uuid}) deployed into ${ewcName}` : `Successfully selected VC deployed into ${ewcName}`;
            AppActions.setSnackbar(msg)
          });
        })
        .catch(err => {
          setDlg(() => ({ open: false }));
          showError(err, `can't deploy vcam into ${ewcName}`);
        })
    }
    let obj = { open: true, dlgType: 'deploy', onOk, onCancel, infraList };
    obj = rowData ? { ...rowData, ...obj } : obj;
    setDlg(obj);
  }, [selectedRows, infraList, filterSelectedType]);

  const onDuplicate = useCallback(rowData => {
    const onCancel = () => setDuplicateDlg(() => ({ open: false }));
    const onOk = () => {
      let promises;
      if(rowData !== undefined && rowData.uuid !== undefined && rowData.uuid !== null)
        promises = [AppActions.duplicateVCam(rowData.uuid)];
      else
        promises = selectedRows?.filter(v => v.type === 'primary')?.map(v => AppActions.duplicateVCam(v.uuid));
      Promise.all(promises)
        .then(() => getVcByFilteredType())
        .then(res => {
          ReactDOM.unstable_batchedUpdates(() => {
            setList(res || []);
            onSelectedRowsReset();
            AppActions.setSnackbar('Successfully selected VC duplicated');
          });
        })
        .catch(err => showError(err, `Can't duplicate selected vcams`))
        .finally(() => onCancel());
    };
    let obj = { open: true, onOk, onCancel};
    if(rowData === undefined) {
      if(selectedRows.length > 0) {
        const selectedRow = selectedRows[0];
        obj = {...selectedRow, ...obj};
      }
    }
    else
      obj = { ...rowData, ...obj };
    setDuplicateDlg(obj);
  }, [selectedRows, infraList, filterSelectedType]);

  const onRemove = useCallback(rowData => {
    const onCancel = () => setRemoveDlg(() => ({ open: false }));
    const onOk = () => {
      let promises;
      if(rowData !== undefined && rowData.uuid !== undefined && rowData.uuid !== null)
        promises = [AppActions.deleteDuplicateVCam(rowData.uuid)];
      else
        promises = selectedRows?.filter(v => v.type === 'secondary')?.map(v => AppActions.deleteDuplicateVCam(v.uuid));
      Promise.all(promises)
        .then(() => getVcByFilteredType())
        .then(res => {
          ReactDOM.unstable_batchedUpdates(() => {
            setList(res || []);
            onSelectedRowsReset();
            AppActions.setSnackbar('Successfully selected VC removed');
          });
        })
        .catch(err => showError(err, `Can't remove selected vcams`))
        .finally(() => onCancel());
    };
    let obj = { open: true, onOk, onCancel};
    if(rowData === undefined) {
      if(selectedRows.length > 0) {
        const selectedRow = selectedRows[0];
        obj = {...selectedRow, ...obj};
      }
    }
    else
      obj = { ...rowData, ...obj };
    setRemoveDlg(obj);
  }, [selectedRows, infraList, filterSelectedType]);

  const onChangeType = useCallback(rowData => {
    const onCancel = () => setDlg(() => ({ open: false }));
    function onOk (type, uuid) {
      const promises = uuid ? [AppActions.updateVcamType(uuid, type)] : selectedRows.map(c => AppActions.updateVcamType(c.uuid, type));
      Promise.all(promises)
        .then(() => getVcByFilteredType())
        .then(res => {
          ReactDOM.unstable_batchedUpdates(() => {
            setList(res.body || []);
            onSelectedRowsReset();
            setDlg(() => ({ open: false }));
            const msg = uuid ? `Successfully VC(${uuid}) type changed into ${type}` : `Successfully selected VC type changed into ${type}`;
            AppActions.setSnackbar(msg);
          });
        })
        .catch(err => {
          setDlg(() => ({ open: false }));
          showError(err, `can't change vcam type`);
        })
    }
    let obj = { open: true, dlgType: 'changeType', onOk, onCancel, infraList };
    obj = rowData ? { ...rowData, ...obj } : obj;
    setDlg(obj);
  }, [selectedRows, filterSelectedType]);

  const onRestart = useCallback(rowData => {
    const onCancel = () => setDlg(() => ({ open: false }));
    function onOk (uuid) {
      const promises = uuid ? [AppActions.restartVcam(uuid)] : selectedRows.map(v => AppActions.restartVcam(v.uuid));
      Promise.all(promises)
        .then(() => getVcByFilteredType())
        .then(res => {
          ReactDOM.unstable_batchedUpdates(() => {
            setList(res || []);
            onSelectedRowsReset();
            setDlg(() => ({ open: false }));
            const msg = uuid ? `Successfully VC(${uuid}) restarted` : 'Successfully selected VC restarted'
            AppActions.setSnackbar(msg);
          });
        })
        .catch(err => {
          setDlg(() => ({ open: false }));
          showError(err, `Can't restart selected vcam`);
        })
    }
    let obj = { open: true, dlgType: 'restart', onOk, onCancel, infraList };
    obj = rowData ? { ...rowData, ...obj } : obj;
    setDlg(obj);
  }, [selectedRows, filterSelectedType]);

  const onClientInfo = useCallback(rowData => {
    const info = rowData.info || {};
    const clients = info.clients || [];
    const onCancel = () => setDlg(() => ({ open: false }));
    let obj = { open: true, dlgType: 'clientInfo', onCancel, clients };
    setDlg(obj);
  }, []);

  const onExpand = useCallback(rowData => {
    return (
      <AppContext.Consumer>
        {({ updateRedirect }) => (
          <VcamsExpanded
            {...rowData}
            onDeploy={onDeploy}
            onRemove={onRemove}
            onRestart={onRestart}
            onDuplicate={onDuplicate}
            onChangeType={onChangeType}
            onClientInfo={onClientInfo}
            updateRedirect={updateRedirect} />
        )}
      </AppContext.Consumer>
    )
  }, [infraList, onDeploy, onChangeType, onRestart]);

  const leftSideToolbarActions = useCallback(() => {
    const buttonsData = [
      {
        name: 'Deploy', openDialog: onDeploy, isDisplayed: () => true
      },
      {
        name: 'Duplicate', openDialog: onDuplicate,
        isDisplayed: (rowData) => rowData.one2mStatus === true && rowData.type === 'primary'
      },
      {
        name: 'Remove', openDialog: onRemove,
        isDisplayed: (rowData) => rowData.one2mStatus === true && rowData.type === 'secondary'
      }
    ];
    const button = (data, index) => (
      <Button key={index} color="primary" variant="contained" style={{marginLeft: 10}} onClick={() => data.openDialog()}>
        {data.name}
      </Button>
    );
    if(selectedRows.length) {
      return (
        <>
          <Tooltip title='Restart'>
            <IconButton color='primary' onClick={() => onRestart()}>
              <RestartIcon />
            </IconButton>
          </Tooltip>
          {
            buttonsData.map((data, index) => {
              let buttonData = null;
              if(selectedRows.length === 1) {
                if(data.isDisplayed(selectedRows[0]))
                  buttonData = button(data, index);
              }
              return buttonData;
            })
          }
        </>
      );
    }
    else
      return null;
  }, [selectedRows]);

  const loader = () => (<div><Loader show={true} /></div>);

  const progressDialog = () => (
    <div id="progressBarContainer">
      <p className="align-center">Upload in progress ({Math.round(progress)}%)</p>
      <LinearProgress variant="determinate" style={{ backgroundColor: '#c7c7c7', margin: '15px 0' }} value={progress} />
    </div>
  );

  function body () {
    setListItemStatus(list);
    setPPStatus(list);
    if (loading) {
      return loader();
    } else {
      return (
        <div className='tab-container vcam-container'>
          <div className='leftFixed'>
            <VcamsFilter filterSelectedType={filterSelectedType} filterselectedItem={filterselectedItem} infraNameList={infraNameList} uuidList={uuidList}
              onAllSelected={onAllSelected}
              onInfraNameSelected={onInfraNameSelected}
              onUuidSelected={onUuidSelected}
            />
          </div>
          <div className='rightFluid vcam'>
            <div>
              {showDropzone ? dropzone() : null}
              {dbSptsStatus ? sptsConfigutationNotification() : null}
            </div>
            {pageLoading ? loader() : viewTable()}
          </div>
          {isProgress ? progressDialog() : null}
          {dlg.open ? <VcamsDialog {...dlg} /> : null}
          {duplicateDlg.open ? <VcamsDuplicateDialog {...duplicateDlg} /> : null}
          {removeDlg.open ? <VcamsRemoveDialog {...removeDlg} /> : null}
        </div>
      );
    }
  }

  const sptsConfigutationNotification = () => {
    const notificationStyle = {display: 'flex', flexDirection: 'row', height: 'fit-content', alignItems: 'center', marginTop: '8px', marginBottom: '4px'}
    const warningIconStyle = {
      height: '24px', width: '24px', verticalAlign: 'middle', marginTop: '-2px', marginRight: '10px', color: 'red'
    }
    const borderStyle = {
      borderColor: '#e7e7e7', borderStyle: 'solid', borderWidth: '2px', backgroundColor: 'inherit', cursor: 'pointer', padding: '12px', width: 'max-content'
    }
    return (
      <div style={{...notificationStyle, ...borderStyle}}>
        <div>
          <span className='icon'>
            <WarningIcon style={warningIconStyle} />
          </span>
        </div>
        <span>
          Multicast IP / SPTS configuration must be updated
        </span>
      </div>
    )
  }

  const dropzone = () => {
    const uploading = AppStore.getUploadInProgress();
    const dropzoneClass = uploading ? 'dropzone disabled muted' : 'dropzone';
    const fileIconStyle = {
      height: '24px', width: '24px', verticalAlign: 'middle', marginTop: '-2px', marginRight: '10px'
    }
    const borderStyle = {
      borderColor: '#e7e7e7', borderStyle: 'solid', borderWidth: '2px', backgroundColor: 'inherit', cursor: 'pointer', padding: '10px', width: 'max-content'
    }
    const createFileName = () => {
      const currentDate = moment().format('YYYY-MM-DD');
      return `spts_configuration_${currentDate}.json`
    }
    return (
      <div style={{display: 'flex', flexDirection: 'row', height: 'fit-content', alignItems: 'center'}}>
        <div>
          <Button
            disabled={isProgress}
            color='primary'
            variant='contained'
            style={{width: 'max-content'}}
            onClick={() => {
              AppActions.exportConfig()
                .then(response => {
                  let data = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(response.body));
                  const link = document.createElement('a');
                  link.download = createFileName();
                  link.href = data;
                  link.click();
                  link.remove();
                })
                .catch((err) => showError(err, `Can't export config.`));
            }}
          >
            Download spts config
          </Button>
        </div>
        <div style={{paddingLeft: '15px'}}>
          <Dropzone
            multiple={false}
            activeClassName='active'
            rejectClassName='active'
            disabled={isProgress}
            onDrop={(accepted, rejected) => onDrop(accepted, rejected)}
          >
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps({className: `${dropzoneClass}`, style: borderStyle})}>
                <input {...getInputProps()} />
                <span className='icon'>
                  <FileIcon style={fileIconStyle} />
                </span>
                <span>
                  Drag here or <a>browse</a> to upload SPTS config
                </span>
              </div>
            )}
          </Dropzone>
        </div>
      </div>
    )
  }

  function onDrop(acceptedFiles, rejectedFiles) {
    const loaded = message => {
      setTimeout(() => {
        setIsProgress(false);
        AppActions.setSnackbar(message, null);
      }, 2000)
    }
    if(acceptedFiles.length) {
      setIsProgress(true);
      const fileName = acceptedFiles[0].name;
      const reader = new FileReader();
      reader.onload = function(e) {
        const contents = e.target.result;
        let importProgress = percent => {
          if(percent !== undefined)
            setProgress(percent)
        }
        AppActions.importConfig(contents, importProgress)
          .then(() => loaded(`File '${fileName}' uploaded successfully`))
          .catch((error) => {
            loaded(`Failed to upload file '${fileName}'.\n${error?.errorMessage?.error}`)
          });
      };
      reader.readAsText(acceptedFiles[0]);
    }
    if(rejectedFiles.length)
      AppActions.setSnackbar(
        `File '${rejectedFiles[0].name}' was rejected. File must be of type .json`,
        null
      );
  }

  function viewTable() {
    return (
      <AppContext.Consumer>
        {({ updateRedirect }) => (
          <CTable
            data={list}
            colDef={colDef}
            onExpand={onExpand}
            redirect={props.redirect}
            selectedRows={selectedRows}
            updateRedirect={updateRedirect}
            onFilterChange={onFilterChange}
            onColumnChange={onColumnChange}
            leftSideToolbarActions={leftSideToolbarActions}
            onRowSelect={!isViewerUser() ? onRowSelect : null}
            onAllRowSelect={!isViewerUser() ? onAllRowSelect : null}
          />
        )}
      </AppContext.Consumer>
    );
  }

  function setPPStatus(list) {
    Array.from(list).forEach(item => {
      let pp_status = 'UNKNOWN', statusOffline = 'OFFLINE';
      let dst_multicast_ip = '--'

      const postProcesses = item.post_processes;
      if(postProcesses !== undefined && postProcesses !== null && postProcesses.length > 0) {
        postProcesses.forEach(ppItem => {
          if(ppItem.type === 'spts') {
            pp_status = item.status !== statusOffline ? ppItem.status : statusOffline;
            dst_multicast_ip = ppItem.dst_multicast_ip;
          }
        });
      }
      item.pp_status = pp_status;
      item.dst_multicast_ip = dst_multicast_ip;
    });
  }

  function fetchDetails (didMount) {
    //Fetching SPTS details
    fetchSptsStatus()

    Promise.all([
      getVcByFilteredType(),
      AppActions.getInfraStructureByPairingStatus(2),
      AppActions.getInfraStructureAllWithSecificFields('name,nodes,pairing_status'),
      AppActions.getUserOrganization(),
      AppActions.getCamerasCount(false)
    ])
      .then(responses => {
        const iNamesList = [];
        const iResponse = responses[2].body || [];
        let sptsStatus = false;
        iResponse.forEach(o => {
          const { name = '', nodes = [], pairing_status } = o;
          iNamesList.push({name, isPaired: pairing_status === 2 });
          nodes.forEach(n => {
            AppActions.getLocalConfigByDeviceUUID(n, 'label').then(lconf => {
              if (uuidList.length > 0) {
                if (!uuidList.map(item => item.uuid).includes(n))
                  uuidList.push({uuid: n, label: lconf.label});
              }
              else
                uuidList.push({uuid: n, label: lconf.label});
            });
          });
        });

        const camerasCountData = responses[4]?.body || {};
        const postProcess = responses[3]?.post_process;
        postProcess
          ?.filter(process => process.type === 'spts')
          ?.forEach(process => sptsStatus = process.status)
        if(sptsStatus && camerasCountData?.count != undefined && camerasCountData?.count != 0) {
          setDbSptsStatus(true)
        }
        setUuidList(uuidList);
        ReactDOM.unstable_batchedUpdates(() => {
          const dataList = responses[0] || [];
          setSptsStatus(dataList);
          setOne2mStatus(dataList);
          setList(dataList);
          setInfraList(responses[1].body || []);
          setInfraNameList(iNamesList);
          if (didMount)
            setLoading(false);
        });
      })
      .catch(err => {
        if (didMount)
          setLoading(false);
        showError(err);
      })
      .finally(() => startTimer());
  }

  function fetchSptsStatus() {
    Promise.all([AppActions.getSptsStatus()])
      .then(response => {
        if (response.body.status && response.body.is_stale) {
          setDbSptsStatus(true)
        }
      }).catch(()=>{})
  }

  function setSptsStatus(list) {
    let sptsStatus = AppStore.getSptsStatus();
    list.forEach(item => item.sptsStatus = sptsStatus);
    colDef
      ?.filter(col => col.title === 'PP Status')
      ?.forEach(col => col.hidden = !sptsStatus);
    colDef
      ?.filter(col => col.title === 'Dst. Multicast IP')
      ?.forEach(col => col.hidden = !sptsStatus);
    setShowDropzone(sptsStatus);
  }

  function setOne2mStatus(list) {
    let one2mStatus = AppStore.getOne2mStatus();
    list.forEach(item => item.one2mStatus = one2mStatus)
  }

  function setListItemStatus(list) {
    Array.from(list).forEach(item => item.status = getStatusByHeartbeat(item.heartbeat, item.info));
  }

  function getStatusByHeartbeat(heartbeat, info) {
    const statusOffline = 'OFFLINE';
    let finalStatus = statusOffline;
    const isNotEmpty = (entity) => entity !== undefined && entity !== null && entity !== '';
    if (isNotEmpty(heartbeat)) {
      const currentDateTime = +Date.now(), heartbeatDateTime = +Date.parse(heartbeat);
      if (currentDateTime > heartbeatDateTime) {
        const timeDifference = currentDateTime - heartbeatDateTime;
        finalStatus = timeDifference > 2 * 60000 ? statusOffline : info.status;
      }
    }
    return finalStatus;
  }

  function refreshData () {
    fetchDetails();
  }

  useEffect(() => fetchDetails(true), []);

  return (<>{body()}</>);
}

export default VcamsTab;
